import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState, PageHeader } from '@/shared/components';
import { toApiError, useApi, type UpdateStripeSettingsRequest } from '@/shared/api';
import { StripeCredentialsCard } from './StripeCredentialsCard';
import { StripeStatusCard } from './StripeStatusCard';
import { getDoctorMessage } from './stripeSettingsPresentation';

const settingsKey = ['stripeSettings'] as const;

export function StripeSettingsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: settingsKey,
    queryFn: api.stripe.getSettings,
  });
  const [form, setForm] = useState<UpdateStripeSettingsRequest>(emptyForm());

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.stripe.updateSettings(form),
    onSuccess: (settings) => {
      setForm(toForm(settings));
      toast.success('Stripe-Konfiguration gespeichert');
      void queryClient.invalidateQueries({ queryKey: settingsKey });
    },
    onError: reportError,
  });
  const doctor = useMutation({
    mutationFn: api.stripe.runDoctor,
    onSuccess: (result) => {
      if (result.status === 'Connected') toast.success(getDoctorMessage(result.reason));
      else toast.error(getDoctorMessage(result.reason));
      void queryClient.invalidateQueries({ queryKey: settingsKey });
    },
    onError: reportError,
  });

  if (isLoading) return <p className="text-ink-soft text-sm">Stripe-Konfiguration wird geladen…</p>;
  if (error || !data)
    return <EmptyState icon="Error" title="Stripe-Konfiguration konnte nicht geladen werden" />;

  return (
    <>
      <PageHeader
        eyebrow="Einstellungen"
        title="Zahlungen über"
        titleAccent="Stripe"
        description="Verbinden Sie das Stripe-Konto dieses Workspace. Kartendaten bleiben bei Stripe; StayOn speichert nur verschlüsselte Zugangsdaten und Zahlungsreferenzen."
      />
      <StripeStatusCard settings={data} />
      <StripeCredentialsCard
        form={form}
        settings={data}
        saving={save.isPending}
        checking={doctor.isPending}
        onChange={(update) => setForm((current) => ({ ...current, ...update }))}
        onSave={() => save.mutate()}
        onCheck={() => doctor.mutate()}
      />
    </>
  );
}

function emptyForm(): UpdateStripeSettingsRequest {
  return { publishableKey: '', secretKey: '', webhookSigningSecret: '', accountLabel: null };
}

function toForm(settings: {
  publishableKey: string | null;
  accountLabel: string | null;
}): UpdateStripeSettingsRequest {
  return {
    publishableKey: settings.publishableKey ?? '',
    secretKey: '',
    webhookSigningSecret: '',
    accountLabel: settings.accountLabel,
  };
}

async function reportError(error: unknown) {
  const apiError = await toApiError(error);
  toast.error(apiError.detail ?? apiError.title);
}
